/*
  Licensed under the Apache License, Version 2.0 (the "License"); you may not use
  this file except in compliance with the License. You may obtain a copy of the
  License at

      https://www.apache.org/licenses/LICENSE-2.0

  Unless required by applicable law or agreed to in writing, software distributed
  under the License is distributed on an "AS IS" BASIS, WITHOUT WARRANTIES OR
  CONDITIONS OF ANY KIND, either express or implied. See the License for the
  specific language governing permissions and limitations under the License.
*/

import React, { Children, cloneElement } from "react";
import { noop } from "lodash";

import makeStyles from "@mui/styles/makeStyles";
import Badge from "@mui/material/Badge";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid2";
import IconMenu from "@mui/icons-material/Menu";
import IconClose from "@mui/icons-material/Close";
import Menu from "@mui/material/Menu";
import useMediaQuery from "@mui/material/useMediaQuery";
import useTheme from "@mui/styles/useTheme";

import { ToggleIcon } from "@marapp/earth-shared";
import { SHORT_VIEWPORT, SHORT_VIEWPORT_QUERY } from "../../theme";

import { Option } from "./Option";

interface IProps {
  label: string;
  logo?: React.ReactNode;
  value?: any;
  checkedGroupsCount?: number;
  groupsCount?: number;
  renderDropdown?: boolean;
  children?: React.ReactNode | React.ReactNodeArray;
  onChange?: (value?: any) => any;
  contentAvailable?: boolean;
}

const useStyles = makeStyles((theme) => {
  return {
    logo: {
      "& img": {
        maxHeight: theme.spacing(6),
        width: "auto",
        marginBottom: theme.spacing(1),
        [SHORT_VIEWPORT]: {
          maxHeight: theme.spacing(4),
          marginBottom: 0,
        },
      },
    },
    menuRoot: {
      paddingLeft: "0px !important",
      minWidth: 280,
      maxWidth: 345,
      [theme.breakpoints.only("xs")]: {
        minWidth: "100%",
        maxWidth: "none",
      },
    },
    menuPopOver: {
      background: "rgba(0,0,0,0.5)",
    },
  };
});

const AppContextSwitcher = (props: IProps) => {
  const {
    label,
    logo,
    value,
    groupsCount = 0,
    checkedGroupsCount = 0,
    renderDropdown = true,
    children,
    onChange = noop,
    contentAvailable,
  } = props;

  const classes = useStyles();
  const theme = useTheme();

  const [anchorEl, setAnchorEl] = React.useState(null);

  const toggleDropdown = (event) =>
    setAnchorEl(contentAvailable ? event.currentTarget : null);

  const closeDropdown = () => setAnchorEl(null);

  const isOpen = !!anchorEl;
  const isNarrowViewport = useMediaQuery(theme.breakpoints.down("sm"));
  const isShortViewport = useMediaQuery(SHORT_VIEWPORT_QUERY);
  const isTinyDevice = isNarrowViewport || isShortViewport;

  const badgeLabel =
    checkedGroupsCount && groupsCount > 1
      ? `${checkedGroupsCount}\u2009/\u2009${groupsCount}`
      : null;

  return (
    <Box
      p={1}
      px={{ xs: 1, sm: isShortViewport ? 1 : 2 }}
      pb={0}
      sx={{
        [SHORT_VIEWPORT]: {
          paddingTop: theme.spacing(0.5),
          paddingBottom: 0,
        },
      }}
    >
      <Grid
        container
        className="marapp-qa-context-switcher"
        justifyContent="space-between"
        wrap="nowrap"
        spacing={2}
      >
        <Grid
          className={`${classes.logo} marapp-qa-logo`}
          onClick={closeDropdown}
        >
          {logo}
        </Grid>
        <Grid>
          {renderDropdown && (
            <>
              <Button
                color="secondary"
                onClick={toggleDropdown}
                className="marapp-qa-menu-button"
                sx={{
                  minWidth: isTinyDevice ? "unset" : "inherit",
                  textAlign: "right",
                  lineHeight: 1.2,
                  "& .MuiButton-endIcon": {
                    ...(isTinyDevice ? { margin: 0 } : {}),
                  },
                }}
                endIcon={
                  <Badge
                    badgeContent={badgeLabel}
                    color="primary"
                    showZero={false}
                    sx={{
                      maxWidth: "50px",
                      textAlign: "right",
                      lineHeight: "1.2",
                      "& .MuiBadge-badge": {
                        fontSize: groupsCount >= 10 ? "0.6rem" : "0.65rem",
                        marginRight: groupsCount >= 10 ? "2px" : "4px",
                        height: "13px",
                        padding: "0 3px",
                        width: "max-content",
                        marginTop: "1px",
                      },
                    }}
                  >
                    <ToggleIcon
                      isOn={isOpen}
                      iconOn={<IconClose />}
                      iconOff={<IconMenu />}
                    />
                  </Badge>
                }
              >
                {isTinyDevice ? "" : label}
              </Button>

              <Menu
                open={isOpen}
                anchorEl={anchorEl}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                transformOrigin={{ vertical: "bottom", horizontal: "right" }}
                marginThreshold={48} // hack to push the menu below the trigger button
                onClose={closeDropdown}
                className="marapp-qa-dropdown marapp-qa-menu-dropdown"
                PopoverClasses={{
                  root: classes.menuPopOver,
                }}
                classes={{
                  list: classes.menuRoot,
                }}
                slotProps={{
                  paper: {
                    sx: isNarrowViewport
                      ? {
                          width: `calc(100vw - ${theme.spacing(2)})`,
                          maxWidth: "none",
                          left: `${theme.spacing(1)} !important`,
                        }
                      : undefined,
                  },
                }}
              >
                {Children.map(children, (child: any) => {
                  if (!child) {
                    return;
                  }
                  const isOptionElement = child.props.value;
                  const selected = child.props.value === value;
                  return isOptionElement
                    ? cloneElement(child, {
                        ...child.props,
                        selected,
                        onClick: (value: any) => {
                          if (!selected) {
                            onChange(value);
                          }
                          closeDropdown();
                        },
                      })
                    : child;
                })}
              </Menu>
            </>
          )}
        </Grid>
      </Grid>
    </Box>
  );
};

// Child components
AppContextSwitcher.Option = Option;

export { AppContextSwitcher };
